/**
 * @file ProductDetailsDrawer.tsx
 *
 * Read-only product detail panel shown in the {@link SideDrawer}. Summarises
 * image, SKU, created date, status, type, category, stock, price, and the
 * (plain-text) descriptions, with Edit and Delete actions. The ref is forwarded
 * to the drawer root so the parent can dismiss it on outside click. The last
 * selected product is kept mounted so the close animation has content.
 */

import { forwardRef } from 'react';
import { Divider, Icon } from '@group.one/react-gravity';
import { FormattedMessage, useIntl } from 'react-intl';
import { adminMessages } from '../i18n/messages';
import type { ShopProduct } from '../api/shop-types';
import { stripHtmlForDisplay } from '../stores/products.store';
import { SideDrawer } from '../components/SideDrawer';
import {
  STATUS_FILTER_LABELS,
  STATUS_INDICATOR,
  STOCK_INDICATOR,
  STOCK_LABELS,
  productPriceDisplay,
  productTypeBadge,
} from './product-list';
import styles from './ProductsView.module.css';

interface ProductDetailsDrawerProps {
  product: ShopProduct | null;
  isOpen: boolean;
  currencySymbol: string;
  onClose: () => void;
  onEdit: (product: ShopProduct) => void;
  onDelete: (product: ShopProduct) => void;
}

/**
 * Renders the product detail drawer.
 *
 * @param props - The product to show, open state, currency, and handlers.
 * @param ref - Forwarded to the drawer root for outside-click detection.
 *
 * @returns The drawer element.
 */
export const ProductDetailsDrawer = forwardRef<
  HTMLDivElement,
  ProductDetailsDrawerProps
>(function ProductDetailsDrawer(
  { product, isOpen, currencySymbol, onClose, onEdit, onDelete },
  ref,
) {
  const intl = useIntl();

  if (!product) {
    return (
      <SideDrawer
        ref={ref}
        isOpen={false}
        testId="product-details-drawer"
        children={null}
      />
    );
  }

  const imageSrc = product.images[0]?.src;
  const price = productPriceDisplay(product, currencySymbol).label;
  const statusIndicator = STATUS_INDICATOR[product.status] ?? 'gv-state-attention';
  const statusLabel = STATUS_FILTER_LABELS[product.status];
  const stockIndicator = STOCK_INDICATOR[product.stock_status] ?? 'gv-state-attention';
  const stockLabel = STOCK_LABELS[product.stock_status]
    ? intl.formatMessage(STOCK_LABELS[product.stock_status])
    : product.stock_status;
  const typeBadge = productTypeBadge(product);
  // The full product carries per-variant rows; count how many sellable variants are out.
  const enabledVariants = (product.variants ?? []).filter((v) => v.enabled);
  const outOfStockVariants = enabledVariants.filter(
    (v) => v.manage_stock && (v.stock_quantity ?? 0) <= 0,
  ).length;
  const stockCount =
    product.stock_summary && product.stock_summary.trackedCount > 0
      ? product.stock_summary.trackedQuantitySum
      : product.stock_status === 'instock'
        ? product.stock_quantity
        : null;
  const allCategories = product.categories.map((c) => c.name).join(', ');
  const createdDate = product.date_created
    ? new Date(product.date_created).toLocaleDateString(undefined, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      })
    : '—';

  return (
    <SideDrawer
      ref={ref}
      isOpen={isOpen}
      label={product.name}
      onClose={onClose}
      testId="product-details-drawer"
    >
      <div className="gv-w-full gv-h-full gv-flex gv-flex-col">
        <div className="gv-flex gv-flex-col gv-p-lg">
          <div className={styles.sidepanelHeader}>
            <p className="gv-text-sm gv-text-bold">{product.name}</p>
            <button
              className="gv-button gv-button-icon gv-mode-condensed"
              type="button"
              data-testid="product-details-close"
              onClick={onClose}
              aria-label={intl.formatMessage(adminMessages.productsClose)}
            >
              <Icon name="close" size="small" />
            </button>
          </div>
        </div>
        <div className={styles.sidepanelBody}>
          <div className="gv-flex gv-flex-col gv-gap-md">
            {imageSrc ? (
              <img src={imageSrc} alt={product.name} className={styles.productImage} />
            ) : (
              <div className={styles.productImageEmpty} />
            )}
            <div className={styles.detailRow}>
              <div className={styles.detailField}>
                <span className="gv-text-sm gv-text-bold">
                  <FormattedMessage {...adminMessages.productsFieldSku} />
                </span>
                <span className="gv-text-sm">{product.sku || '—'}</span>
              </div>
              <div className={styles.detailField}>
                <span className="gv-text-sm gv-text-bold">
                  <FormattedMessage {...adminMessages.productsFieldCreated} />
                </span>
                <span className="gv-text-sm">{createdDate}</span>
              </div>
            </div>
            <div className={styles.detailRow}>
              <div className={styles.detailField}>
                <span className="gv-text-sm gv-text-bold">
                  <FormattedMessage {...adminMessages.productsFieldStatus} />
                </span>
                <div className="gv-text-indicator">
                  <div className={`gv-indicator ${statusIndicator}`} />
                  <span className={`gv-text-sm ${styles.capitalize}`}>
                    {statusLabel ? intl.formatMessage(statusLabel) : product.status}
                  </span>
                </div>
              </div>
              <div className={styles.detailField}>
                <span className="gv-text-sm gv-text-bold">
                  <FormattedMessage {...adminMessages.productsFieldType} />
                </span>
                <span className="gv-text-sm">
                  {intl.formatMessage(typeBadge.message, typeBadge.values)}
                </span>
              </div>
            </div>
            <div className={styles.detailRow}>
              <div className={styles.detailField}>
                <span className="gv-text-sm gv-text-bold">
                  <FormattedMessage {...adminMessages.productsFieldCategory} />
                </span>
                <span className="gv-text-sm">{allCategories || '—'}</span>
              </div>
              <div className={styles.detailField}>
                <span className="gv-text-sm gv-text-bold">
                  <FormattedMessage {...adminMessages.productsFieldStock} />
                </span>
                <div className="gv-text-indicator">
                  <div className={`gv-indicator ${stockIndicator}`} />
                  <span className="gv-text-sm">
                    {stockLabel}
                    {stockCount != null && (
                      <span className="gv-text-muted"> ({stockCount})</span>
                    )}
                  </span>
                </div>
                {outOfStockVariants > 0 && (
                  <span
                    className="gv-caption-sm gv-text-muted"
                    data-testid="product-details-variants-out"
                  >
                    <FormattedMessage
                      {...adminMessages.productsVariantsOutOfStock}
                      values={{ count: outOfStockVariants, total: enabledVariants.length }}
                    />
                  </span>
                )}
              </div>
            </div>
            <div className={styles.detailField}>
              <span className="gv-text-sm gv-text-bold">
                <FormattedMessage {...adminMessages.productsFieldPrice} />
              </span>
              <span className="gv-text-sm">{price ?? '—'}</span>
            </div>
            {product.short_description && (
              <>
                <Divider />
                <div className={styles.detailField}>
                  <span className="gv-text-sm gv-text-bold">
                    <FormattedMessage {...adminMessages.productsDrawerShortDescription} />
                  </span>
                  <p className={`gv-text-sm ${styles.descriptionText}`}>
                    {stripHtmlForDisplay(product.short_description)}
                  </p>
                </div>
              </>
            )}
            {product.description && (
              <>
                <Divider />
                <div className={`${styles.detailField} ${styles.lastField}`}>
                  <span className="gv-text-sm gv-text-bold">
                    <FormattedMessage {...adminMessages.productsDrawerLongDescription} />
                  </span>
                  <p className={`gv-text-sm ${styles.descriptionText}`}>
                    {stripHtmlForDisplay(product.description)}
                  </p>
                </div>
              </>
            )}
          </div>
        </div>
        <div className={styles.sidepanelFooter}>
          <button
            className="gv-button gv-button-cancel"
            type="button"
            data-testid="product-details-delete"
            onClick={() => onDelete(product)}
          >
            <FormattedMessage {...adminMessages.productsDelete} />
          </button>
          <button
            className="gv-button gv-button-primary"
            type="button"
            data-testid="product-details-edit"
            onClick={() => onEdit(product)}
          >
            <FormattedMessage {...adminMessages.productsEdit} />
          </button>
        </div>
      </div>
    </SideDrawer>
  );
});
